/**
 * The evolution price list, with its two factors exposed as parameters.
 *
 * `feeding.ts` is the literal port and stays that way. This module is the same
 * arithmetic written as a *price* rather than as a rate, and generalised over
 * the two constants the shape is made of:
 *
 *     price of the n-th evolution point  =  (1 + a(n-1)) · 10^(k·n) / 200
 *                                            \__________/   \______/
 *                                          the line, a=1     the compounding, k=0.015
 *
 * At `GAME_COST` the two agree exactly — `costEvoRate` reduces to `evoRate` and
 * `costLoopEvo` reproduces `calcFeedEffects`, which is what `model.test.ts`
 * pins. Everything else here exists so the page can move `a` and `k` and show
 * what each one was holding up.
 *
 * `n` is the evolution point being bought, so it runs 1 .. 101 across 0 .. 100%
 * evolution and equals the `e2` of feeding_calculations.lua:33 exactly. Above
 * 100% it is clamped there, which is what freezes the price.
 *
 * Everything is in **raw mutagen**, as elsewhere in the model; the display
 * scaling by 10,000 belongs to the UI.
 */

export interface CostParams {
  /**
   * How much the linear multiplier gains per evolution point. The game's `u`
   * counts 1, 2, 3 …, which is `linear = 1`. At 0 the line is gone and only the
   * compounding remains.
   */
  linear: number;
  /**
   * Base-10 exponent added per evolution point — the `0.015` of
   * feeding_calculations.lua:34. At 0 the compounding is gone and only the line
   * remains.
   */
  compounding: number;
}

/** `feeding_calculations.lua:34`, as parameters. */
export const GAME_COST: CostParams = { linear: 1, compounding: 0.015 };

export const isGameCost = (p: CostParams): boolean =>
  p.linear === GAME_COST.linear && p.compounding === GAME_COST.compounding;

/** Loop guard. Below 100% evolution the real loop takes exactly 100 steps. */
const MAX_STEPS = 5000;

/** `e2` — which evolution point is being bought, 1 at evolution 0. */
export const costPointIndex = (evo: number): number => Math.min(evo, 1) * 100 + 1;

/** The line's contribution at point `n`. `n` itself at the game's `linear = 1`. */
export const costLinearFactor = (n: number, p: CostParams): number => 1 + p.linear * (n - 1);

/** The compounding contribution at point `n`. */
export const costCompoundingFactor = (n: number, p: CostParams): number => 10 ** (p.compounding * n);

/** Raw mutagen the n-th evolution point costs — the two factors multiplied. */
export const costPointPrice = (n: number, p: CostParams): number =>
  (costLinearFactor(n, p) * costCompoundingFactor(n, p)) / 200;

/**
 * dEvo/dMutagen. Identical to `evoRate` in `feeding.ts` at `GAME_COST`, which is
 * the whole point: the page is not modelling a different game, it is modelling
 * the same one with the constants let loose.
 */
export function costEvoRate(evo: number, p: CostParams): number {
  const n = costPointIndex(evo);
  return 1 / (costLinearFactor(n, p) * costCompoundingFactor(n, p) * 0.5);
}

/* --------------------------------------------------------------- properties */

/** The compounding factor's own rise per evolution point: `10^k - 1`. */
export const costInflationPerPoint = (p: CostParams): number => 10 ** p.compounding - 1;

/** Evolution points that double the price, from the compounding alone. */
export function costDoublingPoints(p: CostParams): number {
  const r = 10 ** p.compounding;
  return r > 1 ? Math.LN2 / Math.log(r) : Infinity;
}

/**
 * Evolution at which the two factors contribute equally to how fast the price
 * is rising. The line contributes `a / (1 + a(n-1))` per point and the
 * compounding a flat `k·ln 10`; they meet at `n = 1 + 1/b - 1/a`.
 *
 * Null when one of the two is switched off and there is nothing to cross.
 */
export function costCrossoverEvo(p: CostParams): number | null {
  const b = p.compounding * Math.LN10;
  if (!(b > 0) || !(p.linear > 0)) return null;
  const evo = (1 / b - 1 / p.linear) / 100;
  return evo > 0 && evo < 1 ? evo : null;
}

/* --------------------------------------------------------------- the loop */

export interface CostPoint {
  /** Raw mutagen fed since evolution 0. */
  mutagen: number;
  evo: number;
}

/**
 * The loop's own path from evolution 0 to `maxEvo`, one entry per iteration.
 *
 * This is `calc_feed_effects` read backwards. The loop consumes exactly
 * `max_evo_gain_per_iteration / diminishing_modifier` food per step whenever
 * food is not the binding constraint, so stepping evolution and accumulating
 * the food each step costs walks the identical path — and below 100% each step
 * is exactly one evolution point, which makes the returned series literally the
 * running sum of `costPointPrice`.
 */
export function costLoopCurve(p: CostParams, maxEvo = 1): CostPoint[] {
  const out: CostPoint[] = [{ mutagen: 0, evo: 0 }];
  let evo = 0;
  let mutagen = 0;
  for (let i = 0; i < MAX_STEPS && evo < maxEvo - 1e-12; i += 1) {
    const gain = Math.min(evo < 1 ? 0.01 : evo / 10, maxEvo - evo);
    mutagen += gain / costEvoRate(evo, p);
    evo += gain;
    out.push({ mutagen, evo });
  }
  return out;
}

/** Raw mutagen the loop charges to reach `evo` from 0. */
export function costLoopMutagen(evo: number, p: CostParams): number {
  if (evo <= 0) return 0;
  const curve = costLoopCurve(p, evo);
  return curve[curve.length - 1].mutagen;
}

/**
 * Evolution after feeding `mutagen` from 0 — the loop run the way the game runs
 * it, food-driven. Kept separate from `costLoopCurve` because a cursor lands
 * between two of that curve's steps, and reading a chart must not mean
 * interpolating one.
 */
export function costLoopEvo(mutagen: number, p: CostParams): number {
  let evo = 0;
  let food = Math.max(mutagen, 0);
  for (let i = 0; i < MAX_STEPS && food > 0; i += 1) {
    const rate = costEvoRate(evo, p);
    const step = Math.min(food, (evo < 1 ? 0.01 : evo / 10) / rate);
    evo += step * rate;
    food -= step;
  }
  return evo;
}

/* ---------------------------------------------------------- the closed form */

/**
 * ∫ price dn, the continuous version of the sum the loop performs.
 *
 * With `b = k·ln 10` and the price written as `((1-a) + a·n)·e^(bn)`:
 *
 *     ∫ e^(bn) dn      = e^(bn) / b
 *     ∫ n·e^(bn) dn    = e^(bn) (n/b - 1/b²)
 *
 * At `a = 1` this is the `F(u)` of game-math.md §5.4. With no compounding at all
 * the exponential collapses and the integral is a plain parabola, which is the
 * separate branch below rather than a division by zero.
 */
function antiderivative(n: number, p: CostParams): number {
  const b = p.compounding * Math.LN10;
  const a = p.linear;
  if (b < 1e-12) return ((1 - a) * n + (a * n * n) / 2) / 200;
  return (Math.exp(b * n) * ((1 - a + a * n) / b - a / (b * b))) / 200;
}

/** Raw mutagen to reach `evo`, by integrating the price instead of summing it. */
export function costClosedMutagen(evo: number, p: CostParams): number {
  if (evo <= 0) return 0;
  return antiderivative(costPointIndex(evo), p) - antiderivative(1, p);
}

/**
 * Inverse of `costClosedMutagen`, by bisection.
 *
 * There is a closed form for this direction too — the price is a line times an
 * exponential, so inverting it is a Lambert W — but W is not in the standard
 * library and the curve is strictly increasing, so a bisection is exact to
 * double precision and has no branch cases to get wrong.
 */
export function costClosedEvo(mutagen: number, p: CostParams, maxEvo = 1): number {
  if (mutagen <= 0) return 0;
  if (mutagen >= costClosedMutagen(maxEvo, p)) return maxEvo;
  let lo = 0;
  let hi = maxEvo;
  for (let i = 0; i < 60; i += 1) {
    const mid = (lo + hi) / 2;
    if (costClosedMutagen(mid, p) < mutagen) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/** The closed form sampled by evolution, which needs no inversion to draw. */
export function costClosedCurve(p: CostParams, maxEvo = 1, steps = 240): CostPoint[] {
  return Array.from({ length: steps + 1 }, (_, i) => {
    const evo = (maxEvo * i) / steps;
    return { mutagen: costClosedMutagen(evo, p), evo };
  });
}
